/**
 * EG-73 (achado colateral): torna legível o nome de arquivo que foi GRAVADO
 * lido como latin-1.
 *
 * O QUE ACONTECEU
 *   O multer 1.4.5-lts.1 do gs-survey instancia o busboy 1.6.0 sem
 *   `defParamCharset`, então o `filename` do header Content-Disposition era lido
 *   como latin-1 enquanto o browser mandava UTF-8. O nome já chegava corrompido
 *   ao backend e ia assim para question_response_file.file_name.
 *
 *   No dump de 30/07/2026: 4.683 de 4.772 nomes com acento (98,1%). A
 *   `description` do mesmo upload está íntegra — ela viaja no corpo JSON. Esse
 *   contraste, na mesma linha da mesma tabela, é o que prova que o defeito é do
 *   caminho do nome e não do banco.
 *
 * POR QUE ISSO VIVE NA LEITURA E NÃO NUMA MIGRATION
 *   A porta já foi fechada no backend, então uploads NOVOS nascem corretos. Para
 *   os 4.683 antigos havia duas saídas: corrigir o dado com UPDATE ou corrigir a
 *   leitura. A decisão do TL foi não encostar em dado de produção existente.
 *
 *   Contrapartida assumida: este decodificador de legado fica no caminho de
 *   leitura, e o dado no banco continua sujo. Export ou relatório que leia direto
 *   do Postgres ainda mostra 'matÃ©ria'.
 *
 * DUPLICAÇÃO DELIBERADA — TERCEIRA CÓPIA
 *   As gêmeas vivem em:
 *     gs-survey/src/utils/nome-de-upload.ts          (fecha a porta do upload)
 *     gs-form-survey-ui/src/utils/nome-de-upload.ts  (exibição no painel cliente)
 *   Três repositórios sem pacote comum. Se uma mudar, as outras precisam mudar
 *   junto — os três arquivos se apontam por este comentário, e os três specs
 *   cobrem os mesmos casos justamente para a divergência aparecer.
 */

/**
 * A primitiva: reinterpreta os bytes, mas só quando isso produz UTF-8 válido.
 *
 * NÃO é um decode cru. Um nome que JÁ está correto ('matéria') em bytes latin-1
 * tem um 0xE9 solto, que não é UTF-8 válido — um decode ingênuo o destruiria.
 * Trocaríamos um defeito cosmético por um irreversível.
 *
 * É essa guarda que torna a função IDEMPOTENTE, e a idempotência é o que permite
 * aplicá-la na EXIBIÇÃO: o componente não precisa saber se o arquivo é anterior
 * ou posterior ao conserto do backend.
 */
export function decodificarNomeDeUpload(originalname) {
  if (!originalname) return originalname;

  // Caractere acima de 0xFF significa que a string NÃO é uma sequência de bytes
  // latin-1 — é texto real (emoji, cirílico, CJK). Converter truncaria cada
  // ponto de código a um byte, em silêncio. Sai antes.
  if (/[^\u0000-ÿ]/.test(originalname)) return originalname;

  // A ponte latin-1 -> bytes -> UTF-8 é feita por percent-encoding em vez de
  // TextDecoder: o jsdom do react-scripts não implementa TextDecoder, e um
  // polyfill global só para o teste faria o teste exercitar um caminho diferente
  // do que roda no navegador. decodeURIComponent existe nos dois, e não é
  // deprecated como escape().
  const percentEncoded = Array.from(
    originalname,
    (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')
  ).join('');

  try {
    // decodeURIComponent LANÇA URIError quando os bytes não formam UTF-8 válido.
    // É um sinal mais limpo que o U+FFFD do TextDecoder e diz a mesma coisa: ou
    // o nome já estava correto, ou é latin-1 de verdade. Nos dois casos, mexer
    // piora — devolve como veio.
    return decodeURIComponent(percentEncoded);
  } catch {
    return originalname;
  }
}

/**
 * Resolve o nome de exibição de uma evidência, já legível.
 *
 * A expressão `file?.filename || file?.file_name` estava repetida em 5 pontos
 * deste repositório: os dois componentes FileName, os dois downloads e o nome na
 * confirmação de exclusão. As duas chaves convivem porque o payload da API usa
 * `file_name` e o objeto montado no front usa `filename`.
 *
 * Aplicar a decodificação em cada um dos 5 seria 5 chances de esquecer no
 * próximo ponto que surgir. Aqui a resolução e a decodificação andam juntas.
 *
 * `file_path` fica de fora de propósito: é a chave do objeto no S3, gravada com
 * o nome corrompido. Decodificá-la faria o download apontar para um objeto que
 * não existe.
 */
export function nomeDoArquivo(file, fallback = '') {
  const cru = file?.filename || file?.file_name;
  if (!cru) return fallback;
  return decodificarNomeDeUpload(cru);
}
